import { useRef } from 'react';
import { useLoaderData } from 'react-router';
import { useBoolean } from 'ahooks';
import { useHydrated } from 'remix-utils';
import { cn } from '@rocco/utils/cn';

import { useAppDisplayModeContext } from '@archipro-design/aria';
import ProductAttributeEnum from '@modules/product/config/product-attribute';
import { VariantSelectionProvider } from '@modules/product/context/VariantSelectionContext';
import { VariantOptionProvider } from '@modules/product/context/VariantOptionContext';
import { useTrackProductView } from '@modules/product/hook/use-track-product-view';
import SampleEnquiryPopup from '@modules/product/component/sample-enquiry-popup/SampleEnquiryPopup';
import type { loader } from '~/routes/_app.product.$urlSegment';
import { SectionSpacer } from '~/ui-rocco/pages/home-page/components/SectionSpacer';
import { DetailScrollReveal } from '~/ui-rocco/pages/detail-shared';
import { ProductHeroSection } from './sections/ProductHeroSection';
import { ProductGallerySection } from './sections/ProductGallerySection';
import { ProductPurchaseSection } from './sections/ProductPurchaseSection';
import { ProductMetaRowSection } from './sections/ProductMetaRowSection';
import { useSizesInUse } from './sections/purchase/purchase-flow-parts';
import { ProductSpecAccordionSection } from './sections/ProductSpecAccordionSection';
import { SimilarProductsSection } from './sections/SimilarProductsSection';
import { TaggedProjectsSection } from './sections/TaggedProjectsSection';
import { SpecifiedInSection } from './sections/SpecifiedInSection';
import { ProductEnquiryPrompt } from './sections/ProductEnquiryPrompt';
import { GetMatchedBand } from './sections/GetMatchedBand';
import {
    DetailMobileAppChrome,
    EditorialClosing,
    useIslandHideNearBottom,
} from '~/ui-rocco/pages/detail-shared';
import { ProductDetailsMagicIsland } from './magic-island/ProductDetailsMagicIsland';
import { MobilePhotoViewerMount } from './photo-viewer';

/**
 * Null component — fires the ViewProduct analytics inside the
 * variant provider (so it sees the selected variant), matching the legacy PDP.
 */
const ProductViewTracker = () => {
    const { product } = useLoaderData<typeof loader>();
    useTrackProductView(product);
    return null;
};

/**
 * The editorial closing (the page's last content block) wrapped so the island
 * hides once the footer scrolls up into its berth — the persistent purchase
 * pill should never float over the site footer. Lives inside the island
 * provider (rendered within the section list) so the hook sees the controller.
 */
const EditorialClosingWithIslandBound = () => {
    const ref = useRef<HTMLDivElement>(null);
    useIslandHideNearBottom(ref, true);
    return (
        <div ref={ref}>
            <EditorialClosing />
        </div>
    );
};

/**
 * BC-4505 — revamped editorial product details page.
 *
 * Built section-by-section per `plans/BC-4505-details-page-revamp.md`; the
 * loader is shared with the legacy page and stays unchanged. Rendered in place
 * of the legacy page behind the `details_revamp_2026` feature flag (or
 * `?revamp=1` in local dev).
 *
 * Desktop and mobile order their sections differently (BC-4524, Figma
 * 1855:13305): mobile has no text hero (the product title lives in the
 * purchase block) and runs Gallery → Purchase → Accordion → related carousels
 * → Tagged statement → About the Supplier → also-viewed grid → closing. The
 * display mode comes from server device detection, so the branch is
 * SSR-consistent.
 */
export const ProductPageRevamp = () => {
    const {
        product,
        showSampleEnquiry,
        featuredProjects,
        getMatchedStyleImages,
        getMatchedCategories,
    } = useLoaderData<typeof loader>();
    const { mobile } = useAppDisplayModeContext();

    // `?sample=2` deep link — mirror the legacy page's sample enquiry popup
    // mount (hydration-gated, open on load, closable).
    const hydrated = useHydrated();
    const [sampleEnquiryOpen, { setFalse: closeSampleEnquiry }] =
        useBoolean(showSampleEnquiry);

    // Sections self-hide on empty data, so each conditional section OWNS its
    // leading spacer — otherwise a hidden section leaves its spacers stacked
    // as a dead gap. These predicates mirror the sections' own null checks.
    // Diagrams no longer count toward the gallery — they live in the spec
    // accordion's (02) Specs & Dimensions row.
    const hasGallery =
        (product.LibraryImages?.length ?? 0) > 0 ||
        (product.LibraryVideos?.length ?? 0) > 0;
    // The meta row lists only the sizes a variant actually uses (the same set
    // the purchase drawer can configure), so this predicate has to filter the
    // same way or a size-less product renders a spacer around nothing.
    const metaRowSizes = useSizesInUse();
    const hasMetaRow =
        (product.AttributeOptions ?? []).some(
            (option) =>
                option.Attribute?.ID === ProductAttributeEnum.Colour ||
                option.Attribute?.ID === ProductAttributeEnum.Material,
        ) || metaRowSizes.length > 0;
    const hasTaggedProjects = featuredProjects.FeaturedProjects.edges.some(
        (edge) => edge.node.images?.[0]?.src,
    );

    // Everything below the gallery, shared by the page and overlay orders.
    const desktopBody = (
        <>
            {/* gallery → purchase = Figma "Page spacer / L" (160px @1920). */}
            <SectionSpacer
                height={160}
                mobileHeight={28}
                className="bg-white"
                unit="rem"
            />
            {/* F6 variants live in the F4 ecom modal. */}
            <ProductPurchaseSection />
            {hasMetaRow ? (
                <>
                    {/* purchase → meta row = Figma spacer (100px @1920). */}
                    <SectionSpacer
                        height={100}
                        mobileHeight={24}
                        className="bg-white"
                        unit="rem"
                    />
                    {/* F5 — read-only colours / material / size summary. */}
                    <DetailScrollReveal>
                        <ProductMetaRowSection />
                    </DetailScrollReveal>
                </>
            ) : null}
            {hasTaggedProjects ? (
                <>
                    <SectionSpacer
                        height={160}
                        mobileHeight={59}
                        className="bg-white"
                        unit="rem"
                    />
                    {/* Tagged-projects statement sits above the accordion, per
                        the Figma (1841:10347). */}
                    <TaggedProjectsSection />
                </>
            ) : null}
            {/* Tagged statement → accordion: a fixed 5rem (80px) pause — not the
                vw-scaling spacer, so the gap stays put across viewports. The
                no-tagged path keeps the standard vw gap. */}
            {hasTaggedProjects ? (
                <div aria-hidden="true" className="h-20 w-full bg-white" />
            ) : (
                <SectionSpacer
                    height={120}
                    mobileHeight={48}
                    className="bg-white"
                    unit="rem"
                />
            )}
            {/* About the Supplier, Installers, Similar Products and the
                Get-in-Touch enquiry hook (the spec-representative fallback)
                all live inside this accordion on desktop (auto-numbered rows);
                only mobile renders them standalone (see mobileOrder). The
                closing band follows, so the accordion keeps its hairline. */}
            <ProductSpecAccordionSection />
            {/* Closing — the page's last call is Get Matched: a reader who
                passed every supplier prompt without acting probably won't
                enquire with this supplier. Ships with the revamp (this page
                already renders behind the details_revamp_2026 flag); the white
                module gets a clear run either side so it reads as its own
                moment between the accordion and the closing. */}
            <SectionSpacer
                height={80}
                mobileHeight={80}
                className="bg-white"
                unit="rem"
            />
            <GetMatchedBand
                enquiryType="product"
                styleImages={getMatchedStyleImages}
                productCategories={getMatchedCategories}
            />
            <SectionSpacer
                height={80}
                mobileHeight={59}
                className="bg-white"
                unit="rem"
            />
            {/* Island-bound on desktop too: the enquiry fallback pill must not
                float over the closing or the site footer. */}
            <EditorialClosingWithIslandBound />
        </>
    );

    const desktopOrder = (
        <>
            <ProductHeroSection />
            {hasGallery ? (
                <>
                    {/* Short breath under the compact title — the gallery is
                        the page's real hero (and the morph destination), so
                        it sits as high in the fold as the title allows. */}
                    <SectionSpacer
                        height={48}
                        mobileHeight={24}
                        className="bg-white"
                        unit="rem"
                    />
                    <ProductGallerySection />
                </>
            ) : null}
            {desktopBody}
        </>
    );

    const mobileOrder = (
        <>
            {hasGallery ? (
                <>
                    <ProductGallerySection />
                    <SectionSpacer
                        height={64}
                        mobileHeight={28}
                        className="bg-white"
                        unit="rem"
                    />
                </>
            ) : null}
            {/* The purchase block carries the title + inline selector on
                mobile — no text hero, no read-only meta row. */}
            <ProductPurchaseSection />
            <SectionSpacer
                height={120}
                mobileHeight={48}
                className="bg-white"
                unit="rem"
            />
            <ProductSpecAccordionSection />
            {/* Hook (07) — a mid-page nudge after the depth: manufacture the
                reason to enquire that a generic CTA leaves the user to invent. */}
            <SectionSpacer
                height={120}
                mobileHeight={48}
                className="bg-white"
                unit="rem"
            />
            <ProductEnquiryPrompt
                showPhone
                heading="Have a question about this product?"
                body="The team at {supplier} can help with pricing, availability and lead times."
            />
            {/* (NN) Specified in — mobile's dedicated treatment of the tagged
                projects (the desktop statement's click-to-expand reads as
                plain text on touch). Owns its leading spacer and hairline,
                like Similar Products below. */}
            {hasTaggedProjects ? <SpecifiedInSection /> : null}
            {/* (NN) Similar Products — "More in {category}" as a 2-col grid on
                mobile (replaces the old brand-scoped "Other People also viewed").
                Client-fetched + self-hiding, so it owns its leading spacer.
                About the Supplier and Installers live inside the accordion
                above, like desktop — this is the only standalone section left.
                Numbered after the Specified-in section when that renders. */}
            <SimilarProductsSection numberOffset={hasTaggedProjects ? 1 : 0} />
            {/* Closing — the page's last call is Get Matched, same as desktop:
                a reader who passed every enquiry prompt without acting
                probably won't enquire with this supplier. */}
            <SectionSpacer
                height={120}
                mobileHeight={80}
                className="bg-white"
                unit="rem"
            />
            <GetMatchedBand
                enquiryType="product"
                styleImages={getMatchedStyleImages}
                productCategories={getMatchedCategories}
            />
            <SectionSpacer
                height={160}
                mobileHeight={59}
                className="bg-white"
                unit="rem"
            />
            <EditorialClosingWithIslandBound />
        </>
    );

    return (
        <VariantSelectionProvider product={product}>
            <VariantOptionProvider product={product}>
                <ProductViewTracker />
                {/* Chevron tucked inside the gallery tile: 1rem page pad +
                    1rem inner inset, mirroring the save button top-right. */}
                <DetailMobileAppChrome className="top-8 left-8" />
                <ProductDetailsMagicIsland>
                    <main
                        id="main-content"
                        data-testid="product-page-revamp"
                        className={cn(
                            'bg-white',
                            // Text-first mobile order (no gallery): clear the
                            // app-mode back chevron. Gallery-first pages let
                            // it overlay the first image instead.
                            !hasGallery && 'app-mode-top-clearance',
                        )}
                    >
                        {mobile ? mobileOrder : desktopOrder}
                    </main>
                </ProductDetailsMagicIsland>
                {/* Full-bleed pinch-to-zoom viewer behind every gallery and
                    diagram tap on mobile. Mounted outside the island provider
                    so it covers the page chrome, and inert (no chunk fetched)
                    until the first tap. */}
                {mobile && <MobilePhotoViewerMount />}
                {showSampleEnquiry && hydrated && !!product.Professional && (
                    <SampleEnquiryPopup
                        data={{ product }}
                        open={sampleEnquiryOpen}
                        onClose={closeSampleEnquiry}
                    />
                )}
            </VariantOptionProvider>
        </VariantSelectionProvider>
    );
};
